import type { ReactNode } from 'react';

import { Navigate } from 'react-router-dom';

import { getUserRoleFromToken } from 'src/utils/jwt';

// ----------------------------------------------------------------------

// Client-side ROLE guard for the dashboard routes.
//
// IMPORTANT: this is a UX affordance only. `showForRoles` merely HIDES nav items, so without this a
// user could still type a privileged URL and render the page. The AUTHORITATIVE gate is the server's
// `@PreAuthorize("hasRole('SUPER_ADMIN')")` — the data stays safe even if this guard is bypassed.
//
// Reads the role from the same source `showForRoles` uses (`getUserRoleFromToken`, which normalizes
// the JWT `roles` claim to the single highest ROLE_* value). Wrap this INSIDE the existing
// `RequireAuth` branch so an unauthenticated user is sent to sign-in first, not to /404.

type Props = {
  /** A single role (e.g. 'ROLE_SUPER_ADMIN') or any-of a list. */
  role: string | string[];
  children: ReactNode;
  /** Where to send an authenticated-but-unauthorized user. Defaults to /404. */
  redirectTo?: string;
};

export function RequireRole({ role, children, redirectTo = '/404' }: Props) {
  const currentRole = getUserRoleFromToken();
  const allowed = Array.isArray(role) ? role : [role];

  if (!currentRole || !allowed.includes(currentRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
